import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaCar, FaArrowLeft } from "react-icons/fa";
import { productService, resolveProductMediaUrl, getDocumentUrl } from "../services/apiService";
import { useTheme } from "../contexts/ThemeContext";
import CategoryProductList from "./catalogue/CategoryProductList";

const useQuery = () => new URLSearchParams(useLocation().search);

const PART_PLACEHOLDER =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect width='120' height='120' rx='12' fill='%230f172a'/%3E%3C/svg%3E";

function mapToCatalogProduct(product) {
  const linked =
    product?.productId && typeof product.productId === "object" ? product.productId : null;
  const rawImg =
    (Array.isArray(product.images) && product.images[0]) ||
    product.imageUrl ||
    product.image ||
    linked?.imageUrl ||
    "";
  const image = rawImg
    ? resolveProductMediaUrl(rawImg) || getDocumentUrl(rawImg) || PART_PLACEHOLDER
    : PART_PLACEHOLDER;

  return {
    id: product._id || product.id,
    name: product.name || product.partName || product.title || "Spare Part",
    brand: product.brand || product.manufacturer || "Unknown",
    partNumber: product.partNumber || product.sku || "N/A",
    price: product.price || product.sellingPrice || 0,
    mrp: product.mrp || product.listPrice || null,
    discount: product.discount || 0,
    image,
    images: [image],
    isOEM: product.origin === "OEM" || product.isOEM,
    origin: product.origin || "Aftermarket",
    condition: product.condition || "new",
    usedGrade: product.usedGrade || "",
    usedConditionRaw: product.usedCondition || product.conditionGrade || "",
    stock: product.stock || 0,
    rating:
      typeof product.rating === "number"
        ? product.rating
        : typeof product.averageRating === "number"
          ? product.averageRating
          : 4.2,
    warrantyMonths: product.warrantyMonths ?? null,
    soldBy: product.soldBy || product.sellerName || "",
    deliveryDays: product.deliveryTime ? parseInt(product.deliveryTime, 10) || 4 : 4,
    fulfilledBySparelo: true,
    freeDelivery: Boolean(product.freeDelivery),
    category: product.category?.name || product.categoryName || product.category || "Parts",
  };
}

/**
 * Compatible parts for a selected vehicle — same Screen-2 layout as /catalog
 * (sticky scrollable filters + OEM | Aftermarket | Used columns).
 */
const VehicleSearchResults = () => {
  const query = useQuery();
  const { isDark } = useTheme();
  const maker = query.get("maker") || query.get("vehicleMake") || "";
  const model = query.get("model") || query.get("vehicleModel") || "";
  const year = query.get("year") || query.get("vehicleYear") || "";
  const modification = query.get("mod") || query.get("vehicleVariant") || "";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [listSearch, setListSearch] = useState("");
  const [sortBy, setSortBy] = useState("relevance");

  useEffect(() => {
    const fetchCompatibleProducts = async () => {
      if (!maker && !model) {
        setLoading(false);
        setProducts([]);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const result = await productService.getUserProducts({
          limit: 200,
          vehicleMake: maker,
          vehicleModel: model,
          vehicleYear: year,
          vehicleVariant: modification,
        });
        const fetched = result.products || result.data?.products || result || [];
        setProducts(Array.isArray(fetched) ? fetched : []);
      } catch (err) {
        console.error("Failed to fetch vehicle-compatible products:", err);
        setError("Failed to load compatible parts. Please try again.");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCompatibleProducts();
  }, [maker, model, year, modification]);

  const mappedProducts = useMemo(() => {
    return products.map(mapToCatalogProduct);
  }, [products]);

  const vehicleLabel = [maker, model, year].filter(Boolean).join(" ");

  return (
    <main
      className={`min-h-screen py-6 sm:py-8 md:py-10 px-4 sm:px-6 lg:px-8 ${
        isDark ? "bg-slate-950 text-slate-200" : "bg-slate-50 text-slate-900"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <Link
            to="/catalog"
            className={`inline-flex items-center gap-2 text-sm font-medium ${
              isDark ? "text-amber-400 hover:text-amber-300" : "text-amber-700 hover:text-amber-800"
            }`}
          >
            <FaArrowLeft className="text-xs" />
            Back to catalog
          </Link>
        </div>

        <header className="mb-6">
          <div className="flex items-start gap-3">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                isDark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-700"
              }`}
            >
              <FaCar />
            </div>
            <div>
              <h1
                className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${
                  isDark ? "text-white" : "text-slate-900"
                }`}
              >
                Compatible parts
                {vehicleLabel ? (
                  <>
                    {" "}
                    for <span className="text-amber-400">{vehicleLabel}</span>
                  </>
                ) : null}
              </h1>
              <p className={`mt-1 text-sm ${isDark ? "text-slate-500" : "text-slate-600"}`}>
                Compare OEM, Aftermarket &amp; Used side by side for your vehicle.
              </p>
            </div>
          </div>
        </header>

        {error && (
          <div
            className={`mb-4 rounded-xl border px-4 py-3 text-sm ${
              isDark
                ? "border-rose-500/40 bg-rose-950/40 text-rose-200"
                : "border-rose-200 bg-rose-50 text-rose-700"
            }`}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div
            className={`rounded-2xl border p-16 text-center ${
              isDark ? "border-slate-700 bg-slate-900/60 text-slate-400" : "border-slate-200 bg-white text-slate-500"
            }`}
          >
            Loading compatible parts…
          </div>
        ) : (
          <div
            className={`rounded-2xl border overflow-visible p-3 sm:p-5 md:p-6 ${
              isDark
                ? "border-slate-700/90 bg-slate-900/40 shadow-xl shadow-black/30"
                : "border-slate-200 bg-white shadow-sm"
            }`}
          >
            <CategoryProductList
              categoryName="Compatible Parts"
              categorySlug=""
              products={mappedProducts}
              defaultFilters={{}}
              isEmbedded
              applyUrlOriginToSidebar={false}
              embeddedCatalogChrome
              catalogToolbarInParent
              parentListSearch={listSearch}
              onParentListSearchChange={setListSearch}
              parentSortBy={sortBy}
              onParentSortChange={setSortBy}
            />
          </div>
        )}
      </div>
    </main>
  );
};

export default VehicleSearchResults;
